import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'

import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { ComponentPropsWithoutRef } from 'react'
import type { ButtonProps } from '@/@bothrs-ui/web/components/primitives/button/Button.types'

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoCard
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoCardRootProps = ComponentPropsWithoutRef<'div'>

const MedicationPhotoCardRoot = ({ children, ...props }: MedicationPhotoCardRootProps) => (
  <StRoot {...props}>{children}</StRoot>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoCard.Icon
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoCardIconProps = ComponentPropsWithoutRef<typeof FontAwesomeIcon>

const MedicationPhotoCardIcon = (props: MedicationPhotoCardIconProps) => <StIcon {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoCard.Title
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoCardTitleProps = ComponentPropsWithoutRef<typeof Span>

const MedicationPhotoCardTitle = ({ children, ...props }: MedicationPhotoCardTitleProps) => (
  <StTitle type='BodyMediumBold' {...props}>
    {children}
  </StTitle>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoCard.Description
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoCardDescriptionProps = ComponentPropsWithoutRef<typeof Span>

const MedicationPhotoCardDescription = ({ children, ...props }: MedicationPhotoCardDescriptionProps) => (
  <StDescription type='BodyMediumRegular' {...props}>
    {children}
  </StDescription>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoCard.Notice
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoCardNoticeProps = ComponentPropsWithoutRef<typeof Span>

const MedicationPhotoCardNotice = ({ children, ...props }: MedicationPhotoCardNoticeProps) => (
  <StNotice type='BodySmallRegular' {...props}>
    {children}
  </StNotice>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoCard.Link
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoCardLinkProps = ComponentPropsWithoutRef<'button'>

/** An inline action styled as a text link, e.g. to open more information. */
const MedicationPhotoCardLink = ({ children, ...props }: MedicationPhotoCardLinkProps) => (
  <StLink type='button' {...props}>
    {children}
  </StLink>
)

/* ------------------------------------------------------------------------------------------------- *
 * MedicationPhotoCard.Action
 * ------------------------------------------------------------------------------------------------- */

type MedicationPhotoCardActionProps = Omit<ButtonProps, 'variant'> & { variant?: ButtonProps['variant'] }

const MedicationPhotoCardAction = ({ children, variant = 'Primary', ...props }: MedicationPhotoCardActionProps) => (
  <Button type='button' variant={variant} {...props}>
    {children}
  </Button>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const MedicationPhotoCard = Object.assign(MedicationPhotoCardRoot, {
  Icon: MedicationPhotoCardIcon,
  Title: MedicationPhotoCardTitle,
  Description: MedicationPhotoCardDescription,
  Notice: MedicationPhotoCardNotice,
  Link: MedicationPhotoCardLink,
  Action: MedicationPhotoCardAction,
})

export { MedicationPhotoCard }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  /* Sits at the bottom of the form body, as designed, without a fixed height above it. */
  margin-top: auto;

  ${({ theme }) => `
    gap: ${theme.UI.SpacingPx.Space4};
    padding: ${theme.UI.SpacingPx.Space6};
    border: 1px solid ${theme.Colors.Neutrals.Light};
    border-radius: ${theme.UI.BorderRadius.Small};
  `}
`

const StIcon = styled(FontAwesomeIcon)`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.Colors.Theme.Primary};
`

const StTitle = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

const StDescription = styled(Span)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`

const StNotice = styled(Span)`
  color: ${({ theme }) => theme.Colors.Status.Error};
`

const StLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  ${({ theme }) => theme.Typography.BodyMediumRegular}
  color: ${({ theme }) => theme.Colors.Theme.Primary};
  text-decoration: underline;
`
